import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, TextField, MenuItem, Button,
  CircularProgress, Alert, List, ListItem, ListItemText, Divider,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import EditIcon from '@mui/icons-material/Edit';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Avatar, IconButton, Badge } from '@mui/material';
import axios from 'axios';
import { API_URL, addMySpouse, uploadMyPhoto, linkMe, addMyChild, updateMyDetails, proposeRename } from '../api';

const FamilyPage = ({ currentUser }) => {
  const [me, setMe] = useState(null);           // my user (with person populated)
  const [myPerson, setMyPerson] = useState(null); // my full person doc (with spouses)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Add-spouse form
  const [spouseName, setSpouseName] = useState('');
  const [spouseGender, setSpouseGender] = useState('');
  const [spouseBirthDate, setSpouseBirthDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [allPeople, setAllPeople] = useState([]);
  const [claimSearch, setClaimSearch] = useState('');
  const [linking, setLinking] = useState(false);

  // Add-child form
  const [childName, setChildName] = useState('');
  const [childGender, setChildGender] = useState('');
  const [childBirthDate, setChildBirthDate] = useState('');
  const [childOtherParent, setChildOtherParent] = useState('');
  const [savingChild, setSavingChild] = useState(false);

  // Edit-my-details dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editBirthDate, setEditBirthDate] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const photoInputRef = React.useRef(null);

  // "Suggest a correction" — for a spouse's name (or any eligible person)
  const [correctTarget, setCorrectTarget] = useState(null); // { _id, name }
  const [correctValue, setCorrectValue] = useState('');
  const [correctSaving, setCorrectSaving] = useState(false);

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setSuccess('');
    setUploadingPhoto(true);
    try {
      const data = await uploadMyPhoto(file);
      setSuccess(data.message);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Photo upload failed. Please try again.');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const meRes = await axios.get(`${API_URL}/api/auth/me`);
      setMe(meRes.data.user);
      const personId = meRes.data.user?.person?._id || meRes.data.user?.person;
      if (personId) {
        const pRes = await axios.get(`${API_URL}/api/people/${personId}`);
        setMyPerson(pRes.data);
      } else {
        const allRes = await axios.get(`${API_URL}/api/people`);
        setAllPeople(allRes.data || []);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load your family information.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleLinkMe = async (person) => {
    setError('');
    setSuccess('');
    setLinking(true);
    try {
      const data = await linkMe(person._id);
      setSuccess(data.message);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not link your account.');
    } finally {
      setLinking(false);
    }
  };

  const handleAddChild = async () => {
    setError('');
    setSuccess('');
    setSavingChild(true);
    try {
      const data = await addMyChild({
        name: childName,
        gender: childGender,
        birthDate: childBirthDate || undefined,
        otherParentId: childOtherParent || undefined,
      });
      setSuccess(data.message);
      setChildName(''); setChildGender(''); setChildBirthDate(''); setChildOtherParent('');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not add the child. Please try again.');
    } finally {
      setSavingChild(false);
    }
  };

  const openEdit = () => {
    setEditName(myPerson?.name || '');
    setEditBirthDate(myPerson?.birthDate ? String(myPerson.birthDate).slice(0, 10) : '');
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    setError('');
    setSuccess('');
    setSavingEdit(true);
    try {
      const data = await updateMyDetails({ name: editName, birthDate: editBirthDate });
      setSuccess(data.message);
      setEditOpen(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update your details.');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleSubmitCorrection = async () => {
    if (!correctTarget) return;
    setError('');
    setSuccess('');
    setCorrectSaving(true);
    try {
      const data = await proposeRename(correctTarget._id, correctValue);
      setSuccess(data.message);
      setCorrectTarget(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not submit the correction.');
    } finally {
      setCorrectSaving(false);
    }
  };

  const handleAddSpouse = async () => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const data = await addMySpouse({
        name: spouseName,
        gender: spouseGender,
        birthDate: spouseBirthDate || undefined,
      });
      setSuccess(data.message);
      setSpouseName('');
      setSpouseGender('');
      setSpouseBirthDate('');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not add spouse. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const spouses = myPerson?.spouses || [];
  const notLinked = !me?.person;

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>My Family</Typography>

      {notLinked ? (
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Find yourself in the tree</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your account is not yet linked to your place in the family tree. Search your
              name below and select yourself to unlock your photo, spouse management, and
              more. (New members are linked automatically when they register.)
            </Typography>
            <TextField
              label="Search your name"
              fullWidth
              value={claimSearch}
              onChange={(e) => setClaimSearch(e.target.value)}
            />
            {claimSearch.trim().length >= 3 && (
              <List dense sx={{ maxHeight: 260, overflowY: 'auto', mt: 1 }}>
                {allPeople
                  .filter((p) => p.name && p.name.toLowerCase().includes(claimSearch.trim().toLowerCase()))
                  .slice(0, 10)
                  .map((p) => (
                    <ListItem
                      key={p._id}
                      disableGutters
                      secondaryAction={
                        <Button size="small" variant="outlined" disabled={linking} onClick={() => handleLinkMe(p)}>
                          This is me
                        </Button>
                      }
                    >
                      <ListItemText
                        primary={p.name}
                        secondary={[
                          p.mother?.name ? `Mother: ${p.mother.name}` : null,
                          p.father?.name ? `Father: ${p.father.name}` : null,
                        ].filter(Boolean).join(' • ') || 'Parents not recorded'}
                      />
                    </ListItem>
                  ))}
              </List>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          <Card sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  <IconButton
                    size="small"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    sx={{ bgcolor: '#059669', color: '#fff', '&:hover': { bgcolor: '#047857' } }}
                    aria-label="Change my photo"
                  >
                    {uploadingPhoto ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <PhotoCameraIcon fontSize="small" />}
                  </IconButton>
                }
              >
                <Avatar src={myPerson?.photoPath || undefined} sx={{ width: 72, height: 72, fontSize: 28 }}>
                  {(myPerson?.name || me?.name || '?').charAt(0)}
                </Avatar>
              </Badge>
              <input type="file" accept="image/*" hidden ref={photoInputRef} onChange={handlePhotoChange} />
              <Box>
              <Typography variant="overline" color="text.secondary">Your place in the tree</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{myPerson?.name || me?.person?.name}</Typography>
                <IconButton size="small" onClick={openEdit} aria-label="Edit my details"><EditIcon fontSize="small" /></IconButton>
              </Box>
              {myPerson && (
                <Typography variant="body2" color="text.secondary">
                  {[
                    myPerson.mother?.name ? `Mother: ${myPerson.mother.name}` : null,
                    myPerson.father?.name ? `Father: ${myPerson.father.name}` : null,
                  ].filter(Boolean).join(' • ') || 'Parents not recorded'}
                </Typography>
              )}
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <FavoriteIcon color="error" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>My spouse{spouses.length === 1 ? '' : 's'}</Typography>
              </Box>

              {spouses.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No spouse recorded yet.</Typography>
              ) : (
                <List dense>
                  {spouses.map((s) => (
                    <ListItem key={s._id || s} disableGutters
                      secondaryAction={
                        s.name ? (
                          <IconButton size="small" edge="end" aria-label={`Suggest a correction for ${s.name}`}
                            onClick={() => { setCorrectTarget(s); setCorrectValue(s.name); }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        ) : null
                      }>
                      <ListItemText
                        primary={s.name || 'Family member'}
                        secondary={s.name ? undefined : 'Open the Tree to view details'}
                      />
                    </ListItem>
                  ))}
                </List>
              )}

              <Divider sx={{ my: 2 }} />

              {(myPerson?.gender !== 'MALE' && !['ADMIN', 'PRESIDENT'].includes(currentUser?.role)) ? (
                <Typography variant="body2" color="text.secondary">
                  In our family, a spouse is added by the husband from his account.
                  If your situation needs special handling, please contact the Family President.
                </Typography>
              ) : (
              <>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Add a spouse</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Your spouse will appear in the tree on your node, and can then register their
                own account by finding themselves in the tree. You can add up to two spouses
                yourself — a third requires the Family President.
              </Typography>

              <TextField label="Spouse's full name" fullWidth margin="dense"
                value={spouseName} onChange={(e) => setSpouseName(e.target.value)} />
              <TextField select label="Gender" fullWidth margin="dense"
                value={spouseGender} onChange={(e) => setSpouseGender(e.target.value)}>
                <MenuItem value="FEMALE">Female</MenuItem>
                <MenuItem value="MALE">Male</MenuItem>
              </TextField>
              <TextField label="Date of birth (optional)" type="date" fullWidth margin="dense"
                InputLabelProps={{ shrink: true }} value={spouseBirthDate}
                onChange={(e) => setSpouseBirthDate(e.target.value)} />

              <Button variant="contained" sx={{
                mt: 2, background: 'linear-gradient(135deg, #059669 0%, #065F46 100%)',
              }}
                disabled={saving || !spouseName.trim() || !spouseGender}
                onClick={handleAddSpouse}>
                {saving ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Add spouse'}
              </Button>
              </>
              )}
            </CardContent>
          </Card>
          <Card sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ChildCareIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>My children</Typography>
              </Box>

              {(myPerson?.children || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">No children recorded yet.</Typography>
              ) : (
                <List dense>
                  {(myPerson.children || []).map((c) => (
                    <ListItem key={c._id || c} disableGutters>
                      <ListItemText primary={c.name || 'Family member'} />
                    </ListItem>
                  ))}
                </List>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Add a child</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Record your child in the family tree. Adult children with phones can also
                register themselves through the normal registration.
              </Typography>

              <TextField label="Child's full name" fullWidth margin="dense"
                value={childName} onChange={(e) => setChildName(e.target.value)} />
              <TextField select label="Gender" fullWidth margin="dense"
                value={childGender} onChange={(e) => setChildGender(e.target.value)}>
                <MenuItem value="MALE">Male</MenuItem>
                <MenuItem value="FEMALE">Female</MenuItem>
              </TextField>
              <TextField label="Date of birth (optional)" type="date" fullWidth margin="dense"
                InputLabelProps={{ shrink: true }} value={childBirthDate}
                onChange={(e) => setChildBirthDate(e.target.value)} />
              {(myPerson?.spouses || []).length > 0 && (
                <TextField select label="Other parent (optional)" fullWidth margin="dense"
                  value={childOtherParent} onChange={(e) => setChildOtherParent(e.target.value)}>
                  {(myPerson.spouses || []).map((s) => (
                    <MenuItem key={s._id || s} value={s._id || s}>{s.name || 'Spouse'}</MenuItem>
                  ))}
                  <MenuItem value="">Not listed</MenuItem>
                </TextField>
              )}

              <Button variant="contained" sx={{
                mt: 2, background: 'linear-gradient(135deg, #059669 0%, #065F46 100%)',
              }}
                disabled={savingChild || !childName.trim() || !childGender}
                onClick={handleAddChild}>
                {savingChild ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Add child'}
              </Button>
            </CardContent>
          </Card>
        </>
      )}

      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mt: 1 }}>{success}</Alert>}

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Edit my details</DialogTitle>
        <DialogContent>
          <TextField label="My name (as it should appear)" fullWidth margin="dense"
            value={editName} onChange={(e) => setEditName(e.target.value)} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            A name change is sent to the admin for approval before it appears anywhere.
          </Typography>
          <TextField label="Date of birth" type="date" fullWidth margin="dense"
            InputLabelProps={{ shrink: true }} value={editBirthDate}
            onChange={(e) => setEditBirthDate(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={savingEdit || editName.trim().length < 3}
            onClick={handleSaveEdit}>
            {savingEdit ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Suggest a name correction for a spouse (or anyone eligible) — also
          waits for admin/President approval before it changes anywhere */}
      <Dialog open={Boolean(correctTarget)} onClose={() => setCorrectTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Suggest a correction</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Currently: <b>{correctTarget?.name}</b>
          </Typography>
          <TextField label="Corrected name" fullWidth margin="dense" autoFocus
            value={correctValue} onChange={(e) => setCorrectValue(e.target.value)} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            This is sent to the admin for approval before it changes in the tree.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCorrectTarget(null)}>Cancel</Button>
          <Button variant="contained" disabled={correctSaving || correctValue.trim().length < 2}
            onClick={handleSubmitCorrection}>
            {correctSaving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FamilyPage;
